import React, { useEffect, useState } from 'react';
import { Users, Mail, GraduationCap, MessageSquareText, Download, ShieldAlert, BarChart3, FileText, Radio, LineChart } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { adminSummary, adminMembers, adminSubscribers, adminEnrollments, adminContacts } from '../lib/api';
import BlogManager from '../components/admin/BlogManager';
import LiveManager from '../components/admin/LiveManager';
import AnalyticsPanel from '../components/admin/AnalyticsPanel';

function toCSV(rows, columns) {
  const header = columns.map((c) => `"${c.label}"`).join(',');
  const body = rows
    .map((r) => columns.map((c) => `"${String(r[c.key] ?? '').replace(/"/g, '""')}"`).join(','))
    .join('\n');
  return `${header}\n${body}`;
}

function download(filename, csv) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch (e) { return iso; }
}

const TABS = [
  { key: 'analytics', label: 'Analytics', icon: LineChart },
  { key: 'members', label: 'Members', icon: Users },
  { key: 'subscribers', label: 'Subscribers', icon: Mail },
  { key: 'enrollments', label: 'Enrollments', icon: GraduationCap },
  { key: 'contacts', label: 'Inquiries', icon: MessageSquareText },
  { key: 'content', label: 'Blog', icon: FileText },
  { key: 'live', label: 'Go Live', icon: Radio },
];

const DATA_TABS = ['members', 'subscribers', 'enrollments', 'contacts'];

export default function Admin() {
  const { user, loading, login } = useAuth();
  const [denied, setDenied] = useState(false);
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState('analytics');
  const [data, setData] = useState({ members: [], subscribers: [], enrollments: [], contacts: [] });
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      setBusy(true);
      try {
        const [s, m, sub, en, c] = await Promise.all([
          adminSummary(), adminMembers(), adminSubscribers(), adminEnrollments(), adminContacts(),
        ]);
        if (cancelled) return;
        setSummary(s);
        setData({ members: m, subscribers: sub, enrollments: en, contacts: c });
      } catch (e) {
        if (e?.response?.status === 403) setDenied(true);
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (loading) return <div className="max-w-6xl mx-auto px-5 pt-20 text-center text-[#5B6472]">Loading...</div>;

  if (!user) return (
    <div className="max-w-md mx-auto px-5 pt-24 text-center">
      <ShieldAlert size={40} className="mx-auto text-[#2E6BFF]" />
      <h1 className="font-display font-extrabold text-[#0E1726] text-[26px] mt-4">Admin sign-in required</h1>
      <p className="text-[#5B6472] mt-3">Sign in with the admin account to view the dashboard.</p>
      <button onClick={login} className="mt-6 bg-[#2E6BFF] text-white font-semibold px-6 py-3 rounded-full">Continue with Google</button>
    </div>
  );

  if (denied) return (
    <div className="max-w-md mx-auto px-5 pt-24 text-center">
      <ShieldAlert size={40} className="mx-auto text-[#EF4444]" />
      <h1 className="font-display font-extrabold text-[#0E1726] text-[26px] mt-4">Access restricted</h1>
      <p className="text-[#5B6472] mt-3">This dashboard is only available to the admin account. You're signed in as {user.email}.</p>
    </div>
  );

  const stats = summary ? [
    { label: 'Members', value: summary.members, icon: Users, color: '#2E6BFF' },
    { label: 'Subscribers', value: summary.subscribers, icon: Mail, color: '#10B981' },
    { label: 'Enrollments (paid)', value: `${summary.enrollments_paid}/${summary.enrollments_total}`, icon: GraduationCap, color: '#7C3AED' },
    { label: 'Inquiries', value: summary.contacts, icon: MessageSquareText, color: '#F59E0B' },
  ] : [];

  const exportMap = {
    members: () => download('members.csv', toCSV(data.members, [
      { key: 'name', label: 'Name' }, { key: 'email', label: 'Email' }, { key: 'post_count', label: 'Posts' }, { key: 'created_at', label: 'Joined' },
    ])),
    subscribers: () => download('subscribers.csv', toCSV(data.subscribers, [
      { key: 'email', label: 'Email' }, { key: 'name', label: 'Name' }, { key: 'created_at', label: 'Subscribed' },
    ])),
    enrollments: () => download('enrollments.csv', toCSV(data.enrollments, [
      { key: 'name', label: 'Name' }, { key: 'email', label: 'Email' }, { key: 'payment_status', label: 'Status' }, { key: 'amount', label: 'Amount' }, { key: 'goals', label: 'Goals' }, { key: 'created_at', label: 'Date' },
    ])),
    contacts: () => download('inquiries.csv', toCSV(data.contacts, [
      { key: 'name', label: 'Name' }, { key: 'email', label: 'Email' }, { key: 'topic', label: 'Topic' }, { key: 'message', label: 'Message' }, { key: 'created_at', label: 'Date' },
    ])),
  };

  const maxInterest = Math.max(1, ...(summary?.interest || []).map((i) => i.count));

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-10 pb-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <span className="eyebrow text-[#2E6BFF]">Admin</span>
          <h1 className="font-display font-extrabold text-[#0E1726] text-[32px] tracking-tight mt-1">Dashboard</h1>
        </div>
        <span className="text-sm text-[#5B6472]">Signed in as {user.email}</span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {stats.map((s) => (
          <div key={s.label} className="bg-white rounded-2xl p-5 shadow-[0_20px_50px_-42px_rgba(14,23,38,0.4)]">
            <span className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: `${s.color}1a`, color: s.color }}><s.icon size={18} /></span>
            <div className="font-display font-extrabold text-[#0E1726] text-[26px] mt-3">{s.value}</div>
            <div className="text-sm text-[#5B6472]">{s.label}</div>
          </div>
        ))}
      </div>

      {summary?.interest?.length > 0 && (
        <div className="bg-white rounded-2xl p-6 mt-6 shadow-[0_20px_50px_-44px_rgba(14,23,38,0.4)]">
          <div className="flex items-center gap-2 text-[#0E1726] font-semibold"><BarChart3 size={18} className="text-[#2E6BFF]" /> Interest by topic (from inquiries)</div>
          <div className="mt-4 space-y-3">
            {summary.interest.map((i) => (
              <div key={i.topic} className="flex items-center gap-3">
                <div className="w-52 shrink-0 text-sm text-[#0E1726] truncate">{i.topic}</div>
                <div className="flex-1 bg-[#F4F4F7] rounded-full h-3 overflow-hidden">
                  <div className="h-full rounded-full bg-[#2E6BFF]" style={{ width: `${(i.count / maxInterest) * 100}%` }} />
                </div>
                <div className="w-8 text-right text-sm font-semibold text-[#0E1726]">{i.count}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3 mt-8">
        <div className="flex gap-2 flex-wrap">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} className={`px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 transition-colors ${tab === t.key ? 'bg-[#0E1B2E] text-white' : 'bg-white text-[#0E1726] hover:bg-black/5'}`}>
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>
        <button onClick={exportMap[tab]} className={`bg-[#2E6BFF] text-white font-semibold px-4 py-2 rounded-full inline-flex items-center gap-2 text-sm hover:bg-[#1f5aef] ${DATA_TABS.includes(tab) ? '' : 'hidden'}`}>
          <Download size={15} /> Export CSV
        </button>
      </div>

      {tab === 'content' ? (
        <div className="mt-4"><BlogManager /></div>
      ) : tab === 'live' ? (
        <div className="mt-4"><LiveManager /></div>
      ) : tab === 'analytics' ? (
        <div className="mt-4"><AnalyticsPanel /></div>
      ) : (
        <div className="bg-white rounded-2xl mt-4 shadow-[0_20px_50px_-44px_rgba(14,23,38,0.4)] overflow-x-auto">
          {busy ? (
            <div className="p-10 text-center text-[#5B6472]">Loading data...</div>
          ) : (
            <TableForTab tab={tab} data={data} fmtDate={fmtDate} />
          )}
        </div>
      )}
    </div>
  );
}

function Th({ children }) { return <th className="text-left text-xs font-semibold text-[#5B6472] uppercase tracking-wide px-5 py-3 whitespace-nowrap">{children}</th>; }
function Td({ children }) { return <td className="px-5 py-3 text-sm text-[#0E1726] align-top">{children}</td>; }

function TableForTab({ tab, data, fmtDate }) {
  if (tab === 'members') {
    const rows = data.members;
    if (!rows.length) return <Empty label="No members yet." />;
    return (
      <table className="w-full min-w-[600px]">
        <thead className="border-b border-black/5"><tr><Th>Name</Th><Th>Email</Th><Th>Posts</Th><Th>Joined</Th></tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={i} className="border-b border-black/5 last:border-0">
            <Td><div className="flex items-center gap-2">{r.picture ? <img src={r.picture} alt="" referrerPolicy="no-referrer" className="w-7 h-7 rounded-full object-cover" /> : <span className="w-7 h-7 rounded-full bg-[#2E6BFF]/12 text-[#2E6BFF] text-xs font-bold flex items-center justify-center">{(r.name || '?').charAt(0)}</span>}<span className="font-medium">{r.name || '—'}</span></div></Td>
            <Td>{r.email}</Td><Td>{r.post_count}</Td><Td>{fmtDate(r.created_at)}</Td>
          </tr>
        ))}</tbody>
      </table>
    );
  }
  if (tab === 'subscribers') {
    const rows = data.subscribers;
    if (!rows.length) return <Empty label="No subscribers yet." />;
    return (
      <table className="w-full min-w-[500px]">
        <thead className="border-b border-black/5"><tr><Th>Email</Th><Th>Name</Th><Th>Subscribed</Th></tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={i} className="border-b border-black/5 last:border-0"><Td>{r.email}</Td><Td>{r.name || '—'}</Td><Td>{fmtDate(r.created_at)}</Td></tr>
        ))}</tbody>
      </table>
    );
  }
  if (tab === 'enrollments') {
    const rows = data.enrollments;
    if (!rows.length) return <Empty label="No enrollments yet." />;
    return (
      <table className="w-full min-w-[700px]">
        <thead className="border-b border-black/5"><tr><Th>Name</Th><Th>Email</Th><Th>Status</Th><Th>Amount</Th><Th>Goals</Th><Th>Date</Th></tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={i} className="border-b border-black/5 last:border-0">
            <Td>{r.name || '—'}</Td><Td>{r.email}</Td>
            <Td><span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${r.payment_status === 'paid' ? 'bg-[#10B981]/12 text-[#059669]' : 'bg-[#F59E0B]/12 text-[#B45309]'}`}>{r.payment_status}</span></Td>
            <Td>${r.amount}</Td><Td><span className="line-clamp-2 max-w-[240px] block">{r.goals || '—'}</span></Td><Td>{fmtDate(r.created_at)}</Td>
          </tr>
        ))}</tbody>
      </table>
    );
  }
  const rows = data.contacts;
  if (!rows.length) return <Empty label="No inquiries yet." />;
  return (
    <table className="w-full min-w-[700px]">
      <thead className="border-b border-black/5"><tr><Th>Name</Th><Th>Email</Th><Th>Topic</Th><Th>Message</Th><Th>Date</Th></tr></thead>
      <tbody>{rows.map((r, i) => (
        <tr key={i} className="border-b border-black/5 last:border-0">
          <Td>{r.name || '—'}</Td><Td>{r.email}</Td>
          <Td>{r.topic ? <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#2E6BFF]/10 text-[#2E6BFF]">{r.topic}</span> : '—'}</Td>
          <Td><span className="line-clamp-2 max-w-[260px] block">{r.message}</span></Td><Td>{fmtDate(r.created_at)}</Td>
        </tr>
      ))}</tbody>
    </table>
  );
}

function Empty({ label }) { return <div className="p-10 text-center text-[#5B6472]">{label}</div>; }
